import { listViewStore, isItemPartOperation, getSelectedTableView } from 'owa-mail-list-store';
import {
    lazyMoveItemsBasedOnNodeIds,
    lazyMoveMailListRows,
    lazyMoveMailListRowsToFocusedOrOther,
    lazyAlwaysMoveRowToFocusedOrOther,
} from 'owa-mail-triage-action';
import { getActionSourceFromCommandingViewMode } from 'owa-command-ribbon';
import { lazyOnSingleSelectionChanged } from 'owa-mail-mark-read-actions';
import type FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import { lazyResetFocus } from 'owa-mail-focus-manager';
import { getRootFolderIdForGivenContext } from 'owa-mail-moveto-control/lib/helpers/moveToCommonUtils';
import { getMailboxInfoForFolderId } from 'owa-folders';
import {
    createNewFolder,
    createNewGroupFolder,
} from 'owa-mail-movetofolder-view/lib/components/createNewFolderUtil';
import moveToStore from 'owa-mail-moveto-control/lib/store/store';
import { getGroupIdFromTableQuery, isGroupTableQuery } from 'owa-group-utils';
import { getGroupRootFolderId } from 'owa-group-left-nav-folders-store';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getDefaultLogonEmailAddress } from 'owa-session-store';
import { MailboxInfo } from 'owa-client-ids';

export const onMoveToFolderClicked = (folderId: string) => {
    const tableViewId = listViewStore.selectedTableViewId;
    const tableView = listViewStore.tableViews.get(tableViewId)!;
    let markAsReadAction = Promise.resolve();

    // Mark as read item if user selected only one item, as reading pane
    // cannot show more than one item at a time if there are multiple items selected.
    if (tableView.selectedRowKeys.size === 1) {
        markAsReadAction = lazyOnSingleSelectionChanged.importAndExecute(
            [...tableView.selectedRowKeys.keys()][0],
            tableView,
            true /* isUserNavigation */
        );
    }

    if (isFeatureEnabled('grp-loadFolders') && isGroupTableQuery(tableView?.tableQuery)) {
        // In groups, the folder IDs are present in REST format. Since we are using OWS API to perform Move To in Groups,
        // REST ID needs to be converted to OWS format.
        // Tracking Bug - Bug 131273: Remove OWS to REST ID and vice versa Id conversion logic once platform provides support.
        folderId = folderId.split('-').join('/').split('_').join('+');
    }

    markAsReadAction.then(() => {
        if (isItemPartOperation()) {
            lazyMoveItemsBasedOnNodeIds.import().then(moveItemsBasedOnNodeIds => {
                moveItemsBasedOnNodeIds(
                    listViewStore.expandedConversationViewState.selectedNodeIds,
                    folderId,
                    tableView.id,
                    getActionSourceFromCommandingViewMode()
                );
            });
        } else {
            lazyMoveMailListRows.importAndExecute(
                [...tableView.selectedRowKeys.keys()],
                folderId,
                tableView.id,
                getActionSourceFromCommandingViewMode()
            );
        }
    });
    lazyResetFocus.importAndExecute();
};

export const onCreateNewFolderClicked = (folderName: string) => {
    const tableView = getSelectedTableView();

    if (isFeatureEnabled('grp-loadFolders') && isGroupTableQuery(tableView?.tableQuery)) {
        const groupId = getGroupIdFromTableQuery(tableView?.tableQuery);
        if (groupId) {
            const rootFolderId = getGroupRootFolderId(groupId);
            const mailboxInfo: MailboxInfo = {
                type: 'GroupMailbox',
                userIdentity: getDefaultLogonEmailAddress() ?? '',
                mailboxSmtpAddress: groupId,
            };

            if (rootFolderId) {
                createNewGroupFolder(folderName, rootFolderId, mailboxInfo).then(folderId => {
                    if (folderId) {
                        onMoveToFolderClicked(folderId);
                    }
                });
            }
        }
    } else {
        const { moveToMailboxType, moveToSharedFolderRootId } = moveToStore;

        // Get appropriate root folder id depending on the current moveToMailboxType.
        // Except 'SharedMailbox', all other mbxs have static rootFolderId. But sharedFolder root Id is stored in local store.
        const rootFolderId =
            moveToMailboxType === 'SharedMailbox'
                ? moveToSharedFolderRootId
                : getRootFolderIdForGivenContext(moveToMailboxType);

        if (rootFolderId === undefined) {
            return;
        }

        const mailboxInfo = getMailboxInfoForFolderId(
            rootFolderId,
            false /* routeToAuxIfAuxArchive */
        );

        createNewFolder(folderName, rootFolderId, mailboxInfo).then(folderId => {
            if (folderId) {
                onMoveToFolderClicked(folderId);
            }
        });
    }

    // Dismiss menu
    lazyResetFocus.importAndExecute();
};

export const onMoveToFocusedOtherInboxClicked = (toViewFilter: FocusedViewFilter) => {
    const tableViewId = listViewStore.selectedTableViewId;
    const tableView = listViewStore.tableViews.get(tableViewId)!;

    lazyMoveMailListRowsToFocusedOrOther.importAndExecute(
        [...tableView.selectedRowKeys.keys()],
        tableView.id,
        toViewFilter,
        getActionSourceFromCommandingViewMode()
    );
    lazyResetFocus.importAndExecute();
};

export const onAlwaysMoveToFocusedOtherInboxClicked = (toViewFilter: FocusedViewFilter) => {
    const tableViewId = listViewStore.selectedTableViewId;
    const tableView = listViewStore.tableViews.get(tableViewId)!;

    lazyAlwaysMoveRowToFocusedOrOther.importAndExecute(
        [...tableView.selectedRowKeys.keys()],
        tableView,
        toViewFilter,
        getActionSourceFromCommandingViewMode()
    );
    lazyResetFocus.importAndExecute();
};
